/**
 * Schmale Navigationsleiste (Desktop): dieselben Ziele wie das volle Menü, nur als Symbole mit
 * Tooltip — Suche, Schnellaktionen, Schnellzugriff, Favoriten, Verlauf und je Gruppe ein
 * Ausklappmenü. Was aktiv ist und welche Zahlen dranstehen, rechnet AppLayout; die Leiste zeigt
 * es nur an, damit beide Fassungen nicht auseinanderlaufen.
 */
import { Fragment, useState } from 'react';
import { ActionIcon, Indicator, Menu, ScrollArea, Tooltip, UnstyledButton, type TooltipProps } from '@mantine/core';
import { IconAlertTriangle, IconLayoutSidebarLeftExpand, IconSearch } from '@tabler/icons-react';
import type { NavBadge } from '../../hooks/useNavBadges';
import { isLeafActive, type NavGroup, type NavLeaf } from './navConfig';
import { NAV_COUNT_MAX, NAV_QUICK_ACTIONS, NavCountBadge, navBadgeCount } from './navShared';
import styles from './CompactNavRail.module.css';

// Die Beschriftung steht nur im Tooltip — deshalb auch bei Tastatur-Fokus zeigen (nur :focus-visible).
// Lange Texte (Fehlermeldung am Hub-Hinweis) brechen um statt quer über den Schirm zu laufen.
const TIP: Pick<TooltipProps, 'position' | 'withArrow' | 'events' | 'multiline' | 'maw'> = {
  position: 'right',
  withArrow: true,
  events: { hover: true, focus: true, touch: false },
  multiline: true,
  maw: 280,
};

type NavIcon = NavLeaf['icon'];

interface RailButtonProps {
  label: string;
  icon: NavIcon;
  onClick: () => void;
  active?: boolean;
  count?: number;
  color?: string;
  variant?: 'subtle' | 'light' | 'default';
}

function RailButton({ label, icon: Icon, onClick, active = false, count = 0, color, variant }: RailButtonProps) {
  return (
    <Tooltip label={label} {...TIP}>
      <Indicator inline disabled={count <= 0} label={count} maxValue={NAV_COUNT_MAX} size={16} offset={4} color="red">
        <ActionIcon
          size="lg"
          radius="md"
          variant={variant ?? (active ? 'light' : 'subtle')}
          color={color ?? (active ? 'orange' : 'gray')}
          aria-label={count > 0 ? `${label} (${count})` : label}
          aria-current={active ? 'page' : undefined}
          className={styles.item}
          data-active={active || undefined}
          onClick={onClick}
        >
          <Icon size={18} />
        </ActionIcon>
      </Indicator>
    </Tooltip>
  );
}

interface RailGroupProps {
  group: NavGroup;
  active: boolean;
  pathname: string;
  navBadges: Record<string, NavBadge>;
  onNavigate: (path: string) => void;
}

function RailGroup({ group, active, pathname, navBadges, onNavigate }: RailGroupProps) {
  const [opened, setOpened] = useState(false);
  const Icon = group.icon;
  return (
    // Tooltip am Wrapper, nicht am Knopf: Menu.Target setzt aria-expanded & Co. auf sein Kind —
    // stünde der Tooltip dazwischen, landeten sie am Tooltip statt am Knopf.
    <Tooltip label={group.label} {...TIP} disabled={opened}>
      <span className={styles.tipAnchor}>
        <Menu position="right-start" offset={10} shadow="md" width={250} withinPortal opened={opened} onChange={setOpened}>
          <Menu.Target>
            <ActionIcon
              size="lg"
              radius="md"
              variant={active || opened ? 'light' : 'subtle'}
              color={active ? 'orange' : 'gray'}
              aria-label={group.label}
              className={styles.item}
              data-active={active || undefined}
            >
              <Icon size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown className={styles.groupMenu}>
            <div className={styles.groupMenuTitle} data-active={active || undefined}>
              {group.label}
            </div>
            {group.sections.map((section, sIdx) => (
              <Fragment key={`${group.id}:sec:${sIdx}`}>
                {section.label ? <Menu.Label>{section.label}</Menu.Label> : null}
                {section.items.map((item) => {
                  const itemActive = isLeafActive(item.path, pathname);
                  const count = navBadgeCount(navBadges[item.path]);
                  return (
                    <Menu.Item
                      key={item.path}
                      className={styles.menuItem}
                      data-active={itemActive || undefined}
                      aria-current={itemActive ? 'page' : undefined}
                      leftSection={<item.icon size={15} />}
                      rightSection={count > 0 ? <NavCountBadge count={count} /> : null}
                      onClick={() => onNavigate(item.path)}
                    >
                      {item.label}
                    </Menu.Item>
                  );
                })}
              </Fragment>
            ))}
          </Menu.Dropdown>
        </Menu>
      </span>
    </Tooltip>
  );
}

export interface CompactNavRailProps {
  pathname: string;
  quickLinks: NavLeaf[];
  isQuickLinkActive: (path: string) => boolean;
  favoriteLeaves: NavLeaf[];
  recentLeaves: NavLeaf[];
  navGroups: NavGroup[];
  activeGroupId: string | undefined;
  navBadges: Record<string, NavBadge>;
  /** Hub-Einstellungen nicht ladbar (im vollen Menü ein Hinweis mit „Erneut versuchen"). */
  hubError: string | null;
  onRetryHub: () => void;
  onNavigate: (path: string) => void;
  onOpenSearch: () => void;
  onOpenChangelog: () => void;
  onExpand: () => void;
}

export function CompactNavRail({
  pathname,
  quickLinks,
  isQuickLinkActive,
  favoriteLeaves,
  recentLeaves,
  navGroups,
  activeGroupId,
  navBadges,
  hubError,
  onRetryHub,
  onNavigate,
  onOpenSearch,
  onOpenChangelog,
  onExpand,
}: CompactNavRailProps) {
  const renderLeaf = (item: NavLeaf, keyPrefix: string, active: boolean, count: number) => (
    <RailButton
      key={`${keyPrefix}:${item.path}`}
      label={item.label}
      icon={item.icon}
      active={active}
      count={count}
      onClick={() => onNavigate(item.path)}
    />
  );

  return (
    <div className={styles.rail}>
      {/* Einmal gezeigt, dann folgen die Nachbar-Tooltips ohne Verzögerung — wie in einer Werkzeugleiste. */}
      <Tooltip.Group openDelay={150} closeDelay={80}>
        {/* Sichtbare Scrollleiste, sobald es überläuft: Die Gruppen stehen ganz unten und wären in
            kurzen Fenstern sonst unauffindbar. */}
        <ScrollArea className={styles.scroll} scrollbars="y" scrollbarSize={4} type="auto">
          <div className={styles.items}>
            <RailButton label="Suchen (⌘K)" icon={IconSearch} variant="default" color="gray" onClick={onOpenSearch} />

            <div className={styles.divider} />
            {NAV_QUICK_ACTIONS.map((a) => (
              <RailButton
                key={a.path}
                label={a.tooltip}
                icon={a.icon}
                variant="light"
                color={a.color}
                onClick={() => onNavigate(a.path)}
              />
            ))}
            {hubError ? (
              <RailButton
                label={`Hub-Einstellungen nicht geladen: ${hubError} — klicken zum erneuten Versuch`}
                icon={IconAlertTriangle}
                variant="light"
                color="yellow"
                onClick={onRetryHub}
              />
            ) : null}

            <div className={styles.divider} />
            {quickLinks.map((item) =>
              renderLeaf(
                item,
                'quick',
                isQuickLinkActive(item.path),
                navBadgeCount(navBadges[item.path.split('?')[0]]),
              ),
            )}

            {favoriteLeaves.length > 0 ? <div className={styles.divider} /> : null}
            {favoriteLeaves.map((item) =>
              renderLeaf(item, 'fav', isLeafActive(item.path, pathname), navBadgeCount(navBadges[item.path])),
            )}

            {recentLeaves.length > 0 ? <div className={styles.divider} /> : null}
            {recentLeaves.map((item) =>
              renderLeaf(item, 'recent', isLeafActive(item.path, pathname), navBadgeCount(navBadges[item.path])),
            )}

            <div className={styles.divider} />
            {navGroups.map((group) => (
              <RailGroup
                key={group.id}
                group={group}
                active={group.id === activeGroupId}
                pathname={pathname}
                navBadges={navBadges}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </ScrollArea>

        <div className={styles.footer}>
          <RailButton label="Menü ausklappen" icon={IconLayoutSidebarLeftExpand} color="gray" onClick={onExpand} />
          <Tooltip label={`Was ist neu? (v${__APP_VERSION__})`} {...TIP}>
            <UnstyledButton className={styles.version} onClick={onOpenChangelog} aria-label={`Was ist neu? Version ${__APP_VERSION__}`}>
              v{__APP_VERSION__}
            </UnstyledButton>
          </Tooltip>
        </div>
      </Tooltip.Group>
    </div>
  );
}
